import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { TiptapJson } from 'api/page-template/types';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { SidePane } from 'components/atoms/side-pane';
import { getTipTapJsonFromState } from 'components/organisms/company-page-template/utils/json-from-source';
import { PeoplePageDerivedSignalSourceUpdater } from 'components/organisms/document-editor/extensions/custom-section/ui/sections-type/people-page-section-sources';
import { PeoplePageTemplateState } from '../types';
import { PeoplePageTemplateDocument } from './document';
import { DynamicSection } from './dynamic-section';
import { PeoplePageTemplateFilterSection } from './filter-section';
import { usePeoplePageDerivedSectionSourcesSidePaneState } from './section-source-side-pane-state';
import {
  PeoplePageTemplateEditTemplateTab,
  PeoplePageTemplateEditTemplateTabs,
} from './tab';

const RightSidebar = styled(SidePane).attrs({
  direction: 'right',
})`
  border-left: 1px solid var(--field-border-default);
`;
const CenterContentWrapper = styled.div<{ $isDynamicSection: boolean }>`
  width: ${({ $isDynamicSection }) =>
    $isDynamicSection ? '100%' : 'calc(654px + calc(var(--spacing-06) * 2))'};
  padding: var(--spacing-05) var(--spacing-06) 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const CenterContent = styled.div`
  padding-bottom: var(--spacing-10);
  overflow-y: auto;
  width: 100%;
  border-radius: var(--border-radius-small);
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const Wrapper = styled.div`
  display: flex;
  height: 100%;
  ${RightSidebar} {
    flex-shrink: 0;
  }
`;

interface Props {
  template: PeoplePageTemplateState;
  onChange: (next: Partial<PeoplePageTemplateState>) => void;
  fields: GroupedOptions<FilterableField>[];
  fieldsLookup: GroupFieldsMeta;
}

export const ManagePeoplePageTemplate: FC<Props> = ({
  template,
  onChange,
  fields,
  fieldsLookup,
}) => {
  const onFilterChange = useCallback(
    (filter: GroupedFilter) => {
      onChange({ filter });
    },
    [onChange],
  );
  const onDocumentChange = useCallback(
    (tiptapJson: TiptapJson) => {
      onChange({
        tiptapJson: {
          loading: false,
          json: tiptapJson,
        },
      });
    },
    [onChange],
  );
  const {
    sectionSourceUpdaterProps,
    sectionSourceUpdater,
    closeSectionSourcesSidePane,
  } = usePeoplePageDerivedSectionSourcesSidePaneState();
  const [activeTab, setActiveTab] = useState<PeoplePageTemplateEditTemplateTab>(
    PeoplePageTemplateEditTemplateTab.Template,
  );

  return (
    <Wrapper>
      <ContentWrapper>
        <PeoplePageTemplateEditTemplateTabs
          activeTab={activeTab}
          onChange={setActiveTab}
        />
        <CenterContent>
          <CenterContentWrapper
            $isDynamicSection={
              activeTab === PeoplePageTemplateEditTemplateTab.DynamicSection
            }
          >
            {activeTab === PeoplePageTemplateEditTemplateTab.Template ? (
              <>
                <PeoplePageTemplateFilterSection
                  filter={template.filter}
                  onChange={onFilterChange}
                  fields={fields}
                />
                <PeoplePageTemplateDocument
                  tiptapJson={getTipTapJsonFromState(template.tiptapJson)}
                  onChange={onDocumentChange}
                  sectionSourceUpdater={sectionSourceUpdater}
                />
              </>
            ) : null}
            {activeTab === PeoplePageTemplateEditTemplateTab.DynamicSection ? (
              <DynamicSection
                fields={fields}
                tiptapJson={getTipTapJsonFromState(template.tiptapJson)}
                onDocumentChange={onDocumentChange}
                fieldsLookup={fieldsLookup}
              />
            ) : null}
          </CenterContentWrapper>
        </CenterContent>
      </ContentWrapper>
      <RightSidebar visible={!!sectionSourceUpdaterProps}>
        {sectionSourceUpdaterProps != null && (
          <PeoplePageDerivedSignalSourceUpdater
            {...sectionSourceUpdaterProps}
            onUpdate={(next) => {
              sectionSourceUpdaterProps.onUpdate(next);
              closeSectionSourcesSidePane();
            }}
            onCancel={closeSectionSourcesSidePane}
          />
        )}
      </RightSidebar>
    </Wrapper>
  );
};
